import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { getTaxonomies } from 'services/api/admin-products';
import { AdminCheckbox } from 'shared-components';
import styles from './TaxonomyDropdown.module.css';

// data is a flat list of taxonomy options from the backend
const formatTaxonomyList = (data) => {
  const hashTable = {};
  // this creates a hash map of all of the taxonomy options
  _.forEach(data, (item) => {
    const { id } = item;
    hashTable[id] = item;
    hashTable[id].children = [];
  });
  // tree is an array of taxonomy options with a field called children
  // children is an array of taxonomy options that are the child of the option
  const tree = [];
  _.forOwn(hashTable, (taxonomyOption) => {
    const { parentId } = taxonomyOption;
    if (parentId) {
      // if the element is not at root level, add it to its parent array of children
      hashTable[parentId].children.push(taxonomyOption);
    } else {
      // if element is at root level, add it to first level elements array
      tree.push(taxonomyOption);
    }
  });
  return tree;
};

// this returns an object with taxonomy ids as keys and display names as values
// used to look up display name from taxonomy id
const getDisplayNames = (tree) => {
  const displays = {};
  const formatTree = (prevArr, list) => {
    _.forEach(list, (item) => {
      const arr = [...prevArr];
      arr.push(item.name);
      if (item.children && item.children.length) {
        formatTree(arr, item.children);
      }
      displays[item.id] = arr.join(' > ');
    });
  };
  formatTree([], tree);
  return displays;
};

// this accepts an option's children and the current selected taxonomy id
// returns whether or not the options has a child that is selected
// this is used to display an option as checked if any of its children are checked
const getChildrenSelected = (children, selectedTaxonomyId) => {
  let selected = false;
  const mapTree = (tree) => {
    _.forEach(tree, (item) => {
      const { id, children: itemChildren } = item;
      if (id.toString() === selectedTaxonomyId) {
        selected = true;
      } else if (itemChildren.length) {
        mapTree(itemChildren);
      }
    });
  };
  mapTree(children);
  return selected;
};

// this accepts an option's children and search results - a Set of ids that match the search input
// returns whether or not an option has any children that match the search result
// this is used to render the search results with their parents
const getChildrenSearched = (children, searchResults) => {
  let searched = false;
  const mapTree = (tree) =>
    _.forEach(tree, (item) => {
      const { id, children: itemChildren } = item;
      if (searchResults.has(id)) {
        searched = true;
      } else if (itemChildren.length) {
        mapTree(itemChildren);
      }
    });
  mapTree(children);
  return searched;
};

const CheckboxSection = ({
  checkboxes,
  margin,
  value,
  handleInputChange,
  resetField,
  getCheckboxesRendered,
}) => {
  return (
    <>
      {_.map(checkboxes, (item) => {
        const { id, children, name } = item;
        const selected = id.toString() === value;
        const selectedChild = getChildrenSelected(children, value);
        const clickAction = selected
          ? () => resetField('taxonomyId')
          : handleInputChange;
        if (getCheckboxesRendered(id, children)) {
          return (
            <React.Fragment key={id}>
              <AdminCheckbox
                label={name}
                name="taxonomyId"
                value={id}
                id={id}
                checked={selected || selectedChild}
                style={{ marginLeft: `${margin}px` }}
                onChange={clickAction}
              />
              <CheckboxSection
                checkboxes={children}
                margin={margin + 18}
                value={value}
                resetField={resetField}
                handleInputChange={handleInputChange}
                getCheckboxesRendered={getCheckboxesRendered}
              />
            </React.Fragment>
          );
        }
      })}
    </>
  );
};

CheckboxSection.propTypes = {
  checkboxes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  margin: PropTypes.number.isRequired,
  value: PropTypes.string,
  handleInputChange: PropTypes.func.isRequired,
  resetField: PropTypes.func.isRequired,
  getCheckboxesRendered: PropTypes.func.isRequired,
};

CheckboxSection.defaultProps = {
  value: '',
};

const TaxonomyDropdown = ({ handleInputChange, value, error, resetField }) => {
  const [taxonomies, setTaxonomies] = useState([]);
  const [taxonomiesTree, setTaxonomiesTree] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [displayOptions, setDisplayOptions] = useState({});
  const [selectedDisplay, setSelectedDisplay] = useState(
    'Select One Classification'
  );
  const [searchResults, setSearchResults] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchTaxonomies = async () => {
      const response = await getTaxonomies();
      setTaxonomies(response);
      const tree = formatTaxonomyList(response);
      setTaxonomiesTree(tree);
      setDisplayOptions(getDisplayNames(tree));
    };
    fetchTaxonomies();
  }, []);

  useEffect(() => {
    if (_.isEmpty(value)) {
      setSelectedDisplay('Select One Classification');
    } else {
      setSelectedDisplay(displayOptions[value]);
    }
  }, [value, displayOptions]);

  const toggleDropdown = () => setDropdownOpen((prevState) => !prevState);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    const input = e.target.value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    const reg = new RegExp(input, 'i'); // consecutive letters
    const idList = new Set();
    _.forEach(taxonomies, (item) => {
      if (reg.test(item.name)) {
        idList.add(item.id);
      }
    });
    setSearchResults(idList);
  };

  // this returns a boolean value that represents whether or not a taxonomy option should be displayed
  // a taxonomy option should only be displayed if there is no active search, or if it or it's child matches the search input
  const getCheckboxesRendered = (id, children) => {
    const hasSearch = !_.isEmpty(searchQuery);
    const searched = searchResults.has(id);
    const childSearched = getChildrenSearched(children, searchResults);
    return !hasSearch || (hasSearch && (searched || childSearched));
  };

  return (
    <>
      <div className={styles.dropdownButtonContainer}>
        <span className={styles.label}>Taxonomy</span>
        <button
          className={`${styles.select} ${error ? styles.error : ''}`}
          type="button"
          onClick={toggleDropdown}
        >
          {selectedDisplay}
        </button>
      </div>
      {dropdownOpen && (
        <div
          className={styles.container}
          onMouseLeave={() => {
            setDropdownOpen(false);
            setSearchQuery('');
          }}
        >
          <div className={styles.search}>
            <i className="far fa-search" />
            <input
              value={searchQuery}
              onChange={handleSearch}
              placeholder="Search"
            />
          </div>
          {_.map(taxonomiesTree, (item) => {
            const { children, name, id } = item;
            if (getCheckboxesRendered(id, children)) {
              return (
                <React.Fragment key={id}>
                  <div className={styles.title}>
                    <span>{name}</span>
                  </div>
                  <div className={styles.taxonomyContainer}>
                    <CheckboxSection
                      checkboxes={children}
                      margin={0}
                      value={value}
                      handleInputChange={handleInputChange}
                      resetField={resetField}
                      getCheckboxesRendered={getCheckboxesRendered}
                    />
                  </div>
                </React.Fragment>
              );
            }
          })}
        </div>
      )}
    </>
  );
};

TaxonomyDropdown.propTypes = {
  handleInputChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  resetField: PropTypes.func.isRequired,
};

TaxonomyDropdown.defaultProps = {
  value: '',
};

export default TaxonomyDropdown;
